import React, { useState, useEffect } from 'react'
import { X, Wand2, Image as ImageIcon } from 'lucide-react'
import { DoubaoImageServiceV2 } from '../services/doubaoImageServiceV2.js'
import { RealAIService } from '../services/RealAIService.js'
import toast from 'react-hot-toast'

export const CharacterImageGenerator = ({ character, onClose, onImagesGenerated }) => {
  const [scenePrompt, setScenePrompt] = useState('') // 场景提示词（角色+背景）
  const [isGeneratingScene, setIsGeneratingScene] = useState(false)
  const [generatedScene, setGeneratedScene] = useState(null) // 生成的完整场景图
  const [isOptimizingPrompt, setIsOptimizingPrompt] = useState(false)
  const [artStyle, setArtStyle] = useState('anime-jp') // 艺术风格

  // 解析性格特点
  const parsePersonality = (personality) => {
    const traits = []
    const personalityLower = personality.toLowerCase()
    
    if (personalityLower.includes('友好') || personalityLower.includes('友善')) traits.push('friendly', 'warm')
    if (personalityLower.includes('开朗') || personalityLower.includes('活泼')) traits.push('cheerful', 'energetic')
    if (personalityLower.includes('温柔') || personalityLower.includes('温和')) traits.push('gentle', 'soft')
    if (personalityLower.includes('幽默') || personalityLower.includes('有趣')) traits.push('humorous', 'playful')
    if (personalityLower.includes('成熟') || personalityLower.includes('稳重')) traits.push('mature', 'calm')
    if (personalityLower.includes('可爱') || personalityLower.includes('萌')) traits.push('cute', 'adorable')
    if (personalityLower.includes('性感') || personalityLower.includes('魅惑')) traits.push('alluring', 'charming')
    if (personalityLower.includes('高冷') || personalityLower.includes('冷淡')) traits.push('cool', 'aloof')
    if (personalityLower.includes('害羞') || personalityLower.includes('腼腆')) traits.push('shy', 'bashful')
    if (personalityLower.includes('自信') || personalityLower.includes('强势')) traits.push('confident', 'strong')
    
    if (traits.length === 0) traits.push('friendly', 'approachable')
    
    return traits
  }

  // 提取描述关键词
  const extractKeywords = (description) => {
    const keywords = []
    const descLower = description.toLowerCase()
    
    if (descLower.includes('学生') || descLower.includes('学校')) keywords.push('student', 'school uniform')
    if (descLower.includes('职业') || descLower.includes('工作')) keywords.push('professional', 'working')
    if (descLower.includes('魔法') || descLower.includes('巫师')) keywords.push('magical', 'wizard')
    if (descLower.includes('战士') || descLower.includes('战斗')) keywords.push('warrior', 'combat')
    if (descLower.includes('公主') || descLower.includes('贵族')) keywords.push('princess', 'noble')
    if (descLower.includes('猫') || descLower.includes('动物')) keywords.push('cat', 'animal features')
    if (descLower.includes('长发')) keywords.push('long hair')
    if (descLower.includes('短发')) keywords.push('short hair')
    if (descLower.includes('眼镜')) keywords.push('glasses')
    if (descLower.includes('制服')) keywords.push('uniform')
    
    if (keywords.length === 0) keywords.push('unique character design', 'distinctive appearance')
    
    return keywords
  }

  // 生成背景关键词
  const generateBackgroundKeywords = (personality, description) => {
    const keywords = []
    const personalityLower = personality.toLowerCase()
    const descLower = description.toLowerCase()
    
    if (personalityLower.includes('开朗') || personalityLower.includes('活泼')) {
      keywords.push('bright', 'warm colors', 'sunny')
    } else if (personalityLower.includes('温柔') || personalityLower.includes('温和')) {
      keywords.push('soft', 'pastel colors', 'gentle')
    } else if (personalityLower.includes('高冷') || personalityLower.includes('成熟')) {
      keywords.push('cool', 'elegant', 'sophisticated')
    } else if (personalityLower.includes('神秘') || personalityLower.includes('魔法')) {
      keywords.push('mysterious', 'mystical', 'magical')
    } else {
      keywords.push('neutral', 'balanced colors')
    }
    
    if (descLower.includes('室内') || descLower.includes('房间')) {
      keywords.push('indoor atmosphere')
    } else if (descLower.includes('室外') || descLower.includes('户外')) {
      keywords.push('outdoor atmosphere')
    }
    
    return keywords
  }

  // 艺术风格选项
  const artStyleOptions = [
    { value: 'anime-jp', label: '日漫风格', keywords: 'Japanese anime style, detailed anime art' },
    { value: 'anime-cn', label: '国漫风格', keywords: 'Chinese anime style, donghua style' },
    { value: 'realistic', label: '写实风格', keywords: 'realistic, photorealistic, detailed' },
    { value: 'watercolor', label: '水彩风格', keywords: 'watercolor painting, soft colors' },
    { value: 'oil-painting', label: '油画风格', keywords: 'oil painting, artistic, painterly' },
    { value: 'pixel', label: '像素风格', keywords: 'pixel art, retro game style' },
    { value: 'custom', label: '自定义', keywords: '' }
  ]

  // 使用 AI 生成详细的场景提示词（角色+背景一体）
  const generateDetailedScenePrompt = async (name, personality, description, style) => {
    const styleInfo = artStyleOptions.find(s => s.value === style) || artStyleOptions[0]
    const styleKeywords = styleInfo.keywords
    
    const aiPrompt = `作为一个专业的AI绘画提示词生成专家，请根据以下角色信息生成一个完整的场景提示词（包含角色和背景环境）。

角色信息：
- 姓名：${name}
- 性格：${personality}
- 描述：${description}
- 艺术风格：${styleInfo.label}

要求：
1. 生成中文提示词，描述一个完整的场景
2. 重点描述以下方面：
   【角色部分】
   - 五官特征（眼睛、鼻子、嘴巴、脸型）
   - 发型和发色
   - 服饰细节（衣服款式、颜色、配饰）
   - 饰品（耳环、项链、手镯等）
   - 动作姿态（站在场景中，自然放松的姿态）
   - 表情（根据性格选择合适的表情）
   - 职业特征（如果有）
   - 身材和气质
   
   【背景环境部分】
   - 场景类型（室内/室外）
   - 环境元素（家具、植物、建筑等）
   - 氛围和色调
   - 光线效果
   - 与角色性格呼应的环境细节

3. 提示词格式：用逗号分隔，先描述角色，再描述背景
4. 必须是单个角色，不能出现其他人
5. 风格关键词：${styleKeywords}

请直接输出中文提示词，不要有其他解释。例如：
"一位长发少女，穿着白色连衣裙，温柔的笑容，站在樱花树下，粉色花瓣飘落，柔和的阳光，温馨的氛围，日漫风格，高质量插画"

现在请生成：`

    try {
      const result = await RealAIService.callLanguageModel(aiPrompt, {
        maxTokens: 600,
        temperature: 0.7
      })
      return result.trim()
    } catch (error) {
      console.error('❌ AI场景提示词生成失败:', error)
      // 如果AI生成失败，返回基础提示词
      return `${name}，${description}，${personality}，单人完整场景，${styleKeywords}，高质量插画`
    }
  }

  // 生成提示词 - 使用AI优化
  const generatePrompts = async () => {
    if (!character) return

    const name = character.name || '角色'
    const personality = character.personality || '友好、开朗'
    const description = character.description || '一个有趣的AI角色'
    
    setIsOptimizingPrompt(true)
    toast.loading('AI正在生成场景关键词...', { id: 'optimizing-prompt' })
    
    try {
      // 使用AI生成完整场景提示词（角色+背景）
      const scenePromptText = await generateDetailedScenePrompt(name, personality, description, artStyle)
      
      setScenePrompt(scenePromptText)
      
      toast.success('场景关键词已生成！', { id: 'optimizing-prompt' })
    } catch (error) {
      console.error('❌ 场景提示词生成失败:', error)
      toast.error('关键词生成失败，请重试', { id: 'optimizing-prompt' })
    } finally {
      setIsOptimizingPrompt(false)
    }
  }

  // 组件加载时自动生成提示词
  useEffect(() => {
    if (character && !scenePrompt) {
      generatePrompts()
    }
  }, [character])

  // 生成完整场景图（角色+背景）
  const handleGenerateScene = async () => {
    if (!scenePrompt.trim()) {
      toast.error('请先生成或输入场景关键词')
      return
    }

    try {
      setIsGeneratingScene(true)
      toast.loading('正在生成场景图...', { id: 'generating-scene' })
      
      // 使用16:9比例生成完整场景（适合横屏显示）
      const imageUrl = await DoubaoImageServiceV2.generateImage(scenePrompt, { aspectRatio: '16:9' })
      setGeneratedScene(imageUrl)
      
      toast.success('场景图生成成功！', { id: 'generating-scene' })
    } catch (error) {
      console.error('❌ 生成场景图失败:', error)
      toast.error('生成场景图失败：' + error.message, { id: 'generating-scene' })
    } finally {
      setIsGeneratingScene(false)
    }
  }

  // 确认并保存
  const handleConfirm = () => {
    if (!generatedScene) {
      toast.error('请先生成场景图')
      return
    }

    // 将场景图同时作为avatar和background使用
    onImagesGenerated({
      avatar: generatedScene,
      background: generatedScene
    })
    onClose()
  }

  // 跳过图片生成，使用默认图片
  const handleSkip = () => {
    console.log('⏭️ 用户选择跳过图片生成')
    // 生成默认头像URL（使用UI Avatars服务）
    const defaultAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(character.name)}&background=random&size=512&bold=true`
    
    onImagesGenerated({
      avatar: defaultAvatar,
      background: null // 不设置背景，使用默认背景
    })
    toast.success('已跳过图片生成，使用默认头像')
    onClose()
  }

  // 初始化时生成提示词
  useEffect(() => {
    if (character) {
      generatePrompts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto border border-dark-600">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600 sticky top-0 bg-dark-800 z-10">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wand2 size={24} className="text-primary" />
            生成角色图片
          </h2>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 风格选择 */}
          <div className="bg-dark-700 rounded-lg p-4">
            <label className="block text-sm font-medium text-white mb-3">
              🎨 选择艺术风格
            </label>
            <select
              value={artStyle}
              onChange={(e) => {
                setArtStyle(e.target.value)
                // 切换风格后重新生成提示词
                if (scenePrompt) {
                  generatePrompts()
                }
              }}
              className="w-full bg-dark-600 border border-dark-500 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {artStyleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* 完整场景生成 */}
          <div className="bg-dark-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ImageIcon size={20} className="text-primary" />
              完整场景（角色 + 背景）
            </h3>
            
            <div className="space-y-4">
              {/* 加载进度提示 */}
              {isOptimizingPrompt && (
                <div className="bg-dark-600 rounded-lg p-4 border border-primary/30">
                  <div className="flex items-center gap-3 mb-2">
                    <Wand2 size={16} className="text-primary animate-spin" />
                    <span className="text-white font-medium">AI正在生成场景关键词...</span>
                  </div>
                  <div className="w-full bg-dark-500 rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary to-secondary animate-pulse" style={{ width: '100%' }}></div>
                  </div>
                  <p className="text-dark-300 text-xs mt-2">根据您的角色描述和性格，AI正在创作专属场景...</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  场景关键词
                </label>
                <textarea
                  value={scenePrompt}
                  onChange={(e) => setScenePrompt(e.target.value)}
                  placeholder="AI将自动生成场景关键词，您也可以手动编辑..."
                  rows={4}
                  className="w-full bg-dark-600 border border-dark-500 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                  disabled={isOptimizingPrompt}
                />
                <button
                  onClick={generatePrompts}
                  disabled={isOptimizingPrompt}
                  className="mt-2 text-xs text-primary hover:text-primary/80 underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <Wand2 size={12} />
                  {isOptimizingPrompt ? 'AI生成中...' : '重新生成关键词（AI优化）'}
                </button>
              </div>

              <div className="flex flex-col gap-4">
                <button
                  onClick={handleGenerateScene}
                  disabled={isGeneratingScene || !scenePrompt.trim() || isOptimizingPrompt}
                  className="w-full px-6 py-3 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                >
                  <Wand2 size={20} />
                  {isGeneratingScene ? '正在生成场景图...' : '生成完整场景'}
                </button>

                {generatedScene && (
                  <div className="w-full">
                    <img
                      src={generatedScene}
                      alt="完整场景"
                      className="w-full rounded-lg border-2 border-primary/30 shadow-xl"
                    />
                    <p className="text-dark-300 text-xs mt-2 text-center">
                      ✨ 此图片将用作聊天窗口的完整背景
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="space-y-3 pt-4 border-t border-dark-600">
            {/* 提示信息 */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-400 text-sm text-center">
                💡 您可以跳过图片生成，稍后也可以在角色管理中重新生成
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
              >
                取消创建
              </button>
              <button
                onClick={handleSkip}
                className="flex-1 px-4 py-2 bg-dark-600 hover:bg-dark-500 text-white rounded-lg transition-colors font-medium border border-dark-500"
              >
                跳过生成
              </button>
              <button
                onClick={handleConfirm}
                disabled={!generatedScene}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                确认使用
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

